import { useCallback, useEffect, useState } from "react";
import { useMessaging } from "../../context/MessagingContext";
import { PENDING_STATUSES } from "../../utils/appointmentScheduledMessageDisplay";
import type { Conversation, ScheduledMessage } from "../../types/messaging";
import type { AppointmentScheduledMessage } from "../../types/appointmentScheduledMessages";

export interface UseScheduledMessagesResult {
  /** Staff-composed chat scheduled sends (all pending). */
  chatItems: ScheduledMessage[];
  /** Automated appointment scheduled messages for the linked client (non-cancelled). */
  appointmentItems: AppointmentScheduledMessage[];
  /**
   * Count of UPCOMING scheduled messages (chat sends + pending appointment
   * messages) — drives the composer banner. Excludes terminal appointment rows
   * (SENT / FAILED / SKIPPED) so the banner reflects what is still going out.
   */
  count: number;
  loading: boolean;
  refresh: () => Promise<void>;
}

/**
 * Single source of truth for the chat "Scheduled messages" surface. ONE call to
 * the messaging service returns BOTH the conversation's own chat scheduled sends
 * AND the linked client's automated appointment scheduled messages (across all
 * their appointments) — so the drawer list and the composer banner count are fed
 * from one request and can never drift.
 *
 * The appointment feed is resolved + gated entirely server-side (empty for an
 * unlinked conversation or when the med-spa feature is off), so the surface
 * degrades cleanly with no client-side special-casing.
 */
export function useScheduledMessages(
  conversation: Conversation | null,
): UseScheduledMessagesResult {
  const { listScheduled } = useMessaging();
  const [chatItems, setChatItems] = useState<ScheduledMessage[]>([]);
  const [appointmentItems, setAppointmentItems] = useState<
    AppointmentScheduledMessage[]
  >([]);
  const [loading, setLoading] = useState(false);

  const conversationId = conversation?.conversation_id ?? null;

  const refresh = useCallback(async () => {
    if (!conversationId) {
      setChatItems([]);
      setAppointmentItems([]);
      return;
    }
    setLoading(true);
    try {
      const { scheduled, appointmentScheduled } =
        await listScheduled(conversationId);
      setChatItems(scheduled);
      setAppointmentItems(appointmentScheduled);
    } catch (err) {
      console.error("Failed to load scheduled messages:", err);
    } finally {
      setLoading(false);
    }
  }, [conversationId, listScheduled]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const count =
    chatItems.length +
    appointmentItems.filter((m) => PENDING_STATUSES.has(m.status)).length;

  return { chatItems, appointmentItems, count, loading, refresh };
}
